import { hubPageBg } from './accountHubStyles';
import { useAsUserParam } from './useAsUserParam';
import { FC, useState } from 'react';
import { useNavigate, useParams } from 'react-router';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Stack,
  Typography,
} from '@mui/material';
import ShieldIcon from '@mui/icons-material/Shield';
import PageHeader from 'src/components/NewRenterInterface/PageHeader/PageHeader';
import ProxyBanner from './ProxyBanner';
import { certTypeDisplayLabel } from 'src/components/NewRenterInterface/utils/certTerminology';
import { RENTER_PATHS } from 'src/routes/renterPaths';
import axios from 'src/utils/axios';
import { fDateUTC, fDateTime } from 'src/utils/formatTime';
import useAuth from 'src/hooks/useAuth';
import {
  GENDER_LABELS,
  DISABILITY_LABELS,
  RACE_ETHNICITY_LABELS,
  CITIZENSHIP_LABELS,
} from './personalInfoChoices';

const DataPrivacyPage: FC = () => {
  const { userId } = useParams<{ userId: string }>();
  const { logout } = useAuth();
  const navigate = useNavigate();
  const asUser = useAsUserParam();
  const backRoute = `/${RENTER_PATHS.HOME}/${RENTER_PATHS.ACCOUNT_HUB}/${userId}`;

  const [requestingExport, setRequestingExport] = useState(false);
  const [exportError, setExportError] = useState<string | null>(null);

  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);

  const handleRequestExport = async (): Promise<void> => {
    setRequestingExport(true);
    setExportError(null);
    try {
      const targetUserId = Number(userId);
      const isProxy = asUser !== '';
      const certHistoryUrl = isProxy
        ? `data-vault/members/${targetUserId}/history/certifications/`
        : `data-vault/history/certifications/`;

      const [profileRes, contactRes, historyRes] = await Promise.all([
        axios
          .get(`user_profile/profile/${targetUserId}/`)
          .catch(() => ({ data: null })),
        axios
          .get(`v2/user/data-export/${asUser}`)
          .catch(() => ({ data: null })),
        axios.get(certHistoryUrl).catch(() => ({ data: null })),
      ]);

      const profile = profileRes.data;
      const contact = contactRes.data;
      const history = historyRes.data;

      // Fetch questionnaire answers for each certification
      const certList: { id: number }[] = Array.isArray(history?.results)
        ? history.results
        : Array.isArray(history)
        ? history
        : [];

      const answersBaseUrl = isProxy
        ? `data-vault/members/${targetUserId}/history/certifications`
        : `data-vault/history/certifications`;

      const answersResults = await Promise.all(
        certList.map((c) =>
          axios
            .get(`${answersBaseUrl}/${c.id}/answers/`)
            .catch(() => ({ data: null }))
        )
      );

      const lines: string[] = [];
      const line = (s = ''): void => {
        lines.push(s);
      };
      const section = (title: string): void => {
        line();
        line(`${'='.repeat(60)}`);
        line(`  ${title}`);
        line(`${'='.repeat(60)}`);
      };
      const field = (label: string, value: string | null | undefined): void => {
        line(`  ${label.padEnd(24)} ${value ?? '—'}`);
      };

      line('PRONTO HOUSING — ACCOUNT DATA EXPORT');
      line(`Generated: ${new Date().toLocaleString()}`);

      // ── Personal Information ──────────────────────────────────────
      section('PERSONAL INFORMATION');
      if (profile) {
        field('First Name', profile.first_name || null);
        field('Middle Name', profile.middle_name || null);
        field('Last Name', profile.last_name || null);
        field(
          'Date of Birth',
          profile.date_of_birth ? fDateUTC(profile.date_of_birth) : null
        );
        field('SSN (last 4)', profile.ssn_last_four || null);
        field(
          'Gender',
          profile.gender
            ? GENDER_LABELS[profile.gender] ?? profile.gender
            : null
        );
        field(
          'Disability Status',
          profile.disability_status
            ? DISABILITY_LABELS[profile.disability_status] ??
                profile.disability_status
            : null
        );
        const races: string[] = (profile.race_ethnicity ?? []).map(
          (v: string) => RACE_ETHNICITY_LABELS[v] ?? v
        );
        field('Race / Ethnicity', races.length ? races.join(', ') : null);
        field(
          'Citizenship',
          profile.citizenship_status
            ? CITIZENSHIP_LABELS[profile.citizenship_status] ??
                profile.citizenship_status
            : null
        );
        field(
          'Last Updated',
          profile.updated ? fDateTime(profile.updated) : null
        );
      } else {
        line('  (unavailable)');
      }

      // ── Contact Information ───────────────────────────────────────
      section('CONTACT INFORMATION');
      if (contact) {
        const emails: {
          email: string;
          is_primary: boolean;
          verified: boolean;
        }[] = contact.emails ?? [];
        if (emails.length) {
          line('  Email Addresses:');
          for (const e of emails) {
            line(
              `    ${e.email}  [primary: ${
                e.is_primary ? 'yes' : 'no'
              }, verified: ${e.verified ? 'yes' : 'no'}]`
            );
          }
        } else {
          line('  Email Addresses: none');
        }

        const phones: {
          phone_number: string;
          is_primary: boolean;
          verified: boolean;
        }[] = contact.phones ?? [];
        if (phones.length) {
          line('  Phone Numbers:');
          for (const p of phones) {
            line(
              `    ${p.phone_number}  [primary: ${
                p.is_primary ? 'yes' : 'no'
              }, verified: ${p.verified ? 'yes' : 'no'}]`
            );
          }
        } else {
          line('  Phone Numbers: none');
        }

        field('TOTP Enrolled', contact.totp_enrolled ? 'yes' : 'no');
      } else {
        line('  (unavailable)');
      }

      // ── Application & Re-certification History ─────────────────────
      section('APPLICATION & RE-CERTIFICATION HISTORY');
      const certs: {
        id: number;
        property_name: string;
        unit_label: string;
        cert_type_label: string;
        effective_date: string | null;
        submitted_at: string | null;
        status_display: string;
        is_active: boolean;
      }[] = history?.results ?? history ?? [];
      if (Array.isArray(certs) && certs.length) {
        for (let i = 0; i < certs.length; i++) {
          const c = certs[i];
          line();
          line(
            `  ${certTypeDisplayLabel(c.cert_type_label) || 'Certification'} #${
              c.id
            }`
          );
          field('  Property', c.property_name);
          field('  Unit', c.unit_label);
          field('  Type', c.cert_type_label);
          field('  Status', c.status_display);
          field('  Active', c.is_active ? 'yes' : 'no');
          field('  Effective Date', c.effective_date);
          field('  Submitted', c.submitted_at);

          // Questionnaire answers for this certification
          const answersData = answersResults[i]?.data;
          if (answersData?.sections?.length) {
            line();
            line('  Questionnaire Answers:');
            for (const sec of answersData.sections) {
              line(`    [${sec.name}]`);
              for (const page of sec.pages ?? []) {
                for (const group of page.groups ?? []) {
                  for (const answer of group.answers ?? []) {
                    if (
                      answer.display_value &&
                      answer.display_value !== 'uploaded'
                    ) {
                      line(
                        `      ${(answer.question_label ?? '').padEnd(40)} ${
                          answer.display_value
                        }`
                      );
                    }
                  }
                }
              }
            }
          }
        }
      } else {
        line('  No certifications on record.');
      }

      line();
      line('— End of export —');

      const blob = new Blob([lines.join('\n')], { type: 'text/plain' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `pronto-account-data-${new Date()
        .toISOString()
        .slice(0, 10)}.txt`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (err: unknown) {
      const msg =
        (err as { response?: { data?: { error?: string; detail?: string } } })
          ?.response?.data?.error ??
        (err as { response?: { data?: { detail?: string } } })?.response?.data
          ?.detail ??
        'Could not download your data. Please try again.';
      setExportError(msg);
    } finally {
      setRequestingExport(false);
    }
  };

  const handleDeleteConfirm = async (): Promise<void> => {
    setDeleting(true);
    setDeleteError(null);
    try {
      await axios.post(`v2/user/account/delete/${asUser}`);
      setDeleteOpen(false);
      if (asUser !== '') {
        // Helper deleted a managed user's account — navigate back to the helper
        // landing rather than logging the helper out.
        navigate(`/${RENTER_PATHS.HOME}/${RENTER_PATHS.ACCOUNT_HUB}`, {
          replace: true,
        });
      } else {
        await logout();
      }
    } catch (err: unknown) {
      const msg =
        (err as { response?: { data?: { error?: string } } })?.response?.data
          ?.error ?? 'Could not delete account. Please try again.';
      setDeleteError(msg);
    } finally {
      setDeleting(false);
    }
  };

  return (
    <Box sx={{ backgroundColor: hubPageBg, minHeight: '100%' }}>
      <PageHeader
        title="Data & Privacy"
        icon={<ShieldIcon />}
        backRoute={backRoute}
        subtitle="Control your data"
        hideBackOnDesktop
      />
      <ProxyBanner />

      <Stack gap={2.5} sx={{ p: 3 }}>
        {/* Single card containing both sections — matches K2YyW */}
        <Box
          sx={{
            backgroundColor: 'background.paper',
            borderRadius: '16px',
            boxShadow: '0px 2px 10px rgba(0,0,0,0.063)',
            p: 2,
          }}
        >
          {/* Download My Data */}
          <Stack gap={1.5} sx={{ mb: 2 }}>
            <Typography sx={{ fontSize: 14, fontWeight: 700 }}>
              Download My Data
            </Typography>
            <Typography sx={{ fontSize: 13, color: 'text.secondary' }}>
              Download information about your account as a text file.
            </Typography>
            {exportError && (
              <Alert severity="error" sx={{ py: 0 }}>
                {exportError}
              </Alert>
            )}
            <Button
              variant="outlined"
              disabled={requestingExport}
              onClick={() => void handleRequestExport()}
              startIcon={
                requestingExport ? <CircularProgress size={14} /> : null
              }
              sx={{
                alignSelf: 'flex-start',
                borderRadius: '8px',
                borderColor: 'primary.main',
                color: 'primary.main',
                fontWeight: 500,
                fontSize: 13,
                '&:hover': {
                  borderColor: 'primary.dark',
                  backgroundColor: 'primary.lighter',
                },
              }}
            >
              <span>
                {requestingExport ? 'Preparing…' : 'Download My Data'}
              </span>
            </Button>
          </Stack>

          <Divider />

          {/* Delete My Account */}
          <Stack gap={1.5} sx={{ mt: 2 }}>
            <Typography sx={{ fontSize: 14, fontWeight: 700 }}>
              Delete My Account
            </Typography>
            <Typography sx={{ fontSize: 13, color: 'text.secondary' }}>
              Permanently delete your account and data.
            </Typography>
            <Button
              variant="outlined"
              onClick={() => setDeleteOpen(true)}
              sx={{
                alignSelf: 'flex-start',
                borderRadius: '8px',
                borderColor: 'error.main',
                color: 'error.dark',
                fontWeight: 500,
                fontSize: 13,
                '&:hover': {
                  borderColor: 'error.dark',
                  backgroundColor: 'error.lighter',
                },
              }}
            >
              Delete Account
            </Button>
            <Typography
              sx={{ fontSize: 12, color: 'text.secondary', lineHeight: 1.5 }}
            >
              Certification data required for regulatory audits will be retained
              per the Terms of Service. You may receive communications with a
              login link, but you will not be able to log in.
            </Typography>
          </Stack>
        </Box>
      </Stack>

      {/* Delete confirmation dialog */}
      <Dialog
        open={deleteOpen}
        onClose={() => setDeleteOpen(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Delete Account</DialogTitle>
        <DialogContent>
          <Stack gap={1.5}>
            <Typography sx={{ fontSize: 14 }}>
              Are you sure you want to permanently delete your account? This
              cannot be undone.
            </Typography>
            {deleteError && (
              <Typography sx={{ fontSize: 13, color: 'error.dark' }}>
                {deleteError}
              </Typography>
            )}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteOpen(false)} disabled={deleting}>
            Cancel
          </Button>
          <Button
            color="error"
            variant="contained"
            onClick={() => void handleDeleteConfirm()}
            disabled={deleting}
            startIcon={deleting ? <CircularProgress size={16} /> : null}
          >
            <span>{deleting ? 'Deleting…' : 'Delete Account'}</span>
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default DataPrivacyPage;
